/**
 * Ondia — centrale consent-configuratie.
 *
 * Dit is de enige plek waar consent-gedrag wordt gedefinieerd voor alle
 * Ondia-properties (marketing-site, app, platform, support).
 *
 * Wijzig je iets aan de trackers? Bump REVISION. Dan wordt op alle
 * properties opnieuw toestemming gevraagd, in één keer.
 */

/** Bump bij elke wijziging in trackers of cookiebeleid. Nooit verlagen. */
export const REVISION = 1;

/**
 * Domeinen waarop de toestemming gedeeld wordt. Eén cookie per domein.
 *
 * Let op: een cookie kan niet over TLD's heen gedeeld worden. Een bezoeker
 * die op app.ondia.app toestemming geeft, krijgt op www.ondia.be dus opnieuw
 * de banner. Dat is browsergedrag, geen bug. Zie README > Domeinen.
 */
export const SHARED_DOMAINS = ['ondia.be', 'ondia.app'];

export const COOKIE_NAME = 'ondia_consent';

/**
 * Cookie-domein, afgeleid van de huidige host.
 *
 * Op *.ondia.be zetten we '.ondia.be', op *.ondia.app '.ondia.app', zodat alle
 * properties binnen hetzelfde domein dezelfde toestemming lezen.
 *
 * Overal anders vallen we terug op de huidige hostname. Zonder deze fallback
 * weigert de browser de cookie stil op localhost en op *.webflow.io staging —
 * de banner blijft dan bij elke pageload terugkomen en dat kost je een middag
 * debuggen.
 */
export function cookieDomain(hostname) {
  const host =
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : SHARED_DOMAINS[0]);
  for (const domain of SHARED_DOMAINS) {
    if (host === domain || host.endsWith(`.${domain}`)) {
      return `.${domain}`;
    }
  }
  return host;
}

/** Alleen Nederlands. Wil je FR/EN terug: JSON toevoegen in public/i18n, hier opnemen, en in index.js autoDetect: 'browser' zetten. */
export const LOCALES = ['nl'];

/**
 * Categorieën. De sleutels worden gebruikt in:
 *   - data-category op script-tags
 *   - linkedCategory in de i18n-secties
 *   - de mapping naar Google Consent Mode (google-consent.js)
 *
 * 'necessary' is read-only: geen toggle, altijd aan.
 */
function baseCategories() {
  const domain = cookieDomain();

  return {
  necessary: {
    enabled: true,
    readOnly: true,
  },

  analytics: {
    autoClear: {
      cookies: [
        { name: /^_ga/, domain },
        { name: '_gid', domain },
        { name: /^ph_/, domain },
      ],
    },
  },

  marketing: {
    autoClear: {
      cookies: [
        { name: /^_gcl/, domain },
        { name: '_fbp', domain },
        { name: 'li_sugr', domain },
        { name: 'bcookie', domain },
      ],
    },
  },
  };
}

/**
 * Per property: welke individueel schakelbare services zijn er?
 *
 * Property-namen komen uit window.ONDIA_CONSENT.property (Webflow) of uit
 * het options-object (apps). Onbekende property -> alleen de basiscategorieën.
 *
 * Let op: 'services' maakt aparte toggles in het voorkeurenpaneel. Gebruik
 * dit alleen waar de gebruiker echt per dienst moet kunnen kiezen; te veel
 * toggles maakt het paneel onleesbaar.
 */
export const PROPERTY_SERVICES = {
  'marketing-site': {
    analytics: {
      ga4: { label: 'Google Analytics 4' },
    },
    marketing: {
      google_ads: { label: 'Google Ads' },
      linkedin: { label: 'LinkedIn Insight Tag' },
    },
  },

  app: {
    analytics: {
      ga4: { label: 'Google Analytics 4' },
      posthog: { label: 'PostHog (productgebruik)' },
    },
  },

  platform: {
    analytics: {
      ga4: { label: 'Google Analytics 4' },
      posthog: { label: 'PostHog (productgebruik)' },
    },
  },

  support: {
    analytics: {
      ga4: { label: 'Google Analytics 4' },
    },
  },
};

/**
 * Bouwt het volledige categories-object voor één property.
 */
export function categoriesFor() {
  // Bewust geen services (aparte toggles per tracker): één schakelaar per
  // categorie. PROPERTY_SERVICES blijft documentatie van wat er per property
  // draait.
  const result = {};
  for (const [name, definition] of Object.entries(baseCategories())) {
    result[name] = { ...definition };
  }
  return result;
}
